if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };

        this.mql = window.matchMedia('(min-width: 750px)');
        this.colorOptionNames = ['color', 'colour', 'farve'];
        this.currentGroup = null;
        this.ignoreGalleryScrollUntil = 0;
        this.isProgrammaticChange = false;

        this.injectKlaraThumbnailOverlayStyle();

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 120));
        }

        if (this.elements.thumbnails) {
          this.bindThumbnailClicks();

          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
            this.removeListSemantic();
          }
        }

        this.bindVariantAndSwatchChanges();

        window.setTimeout(() => {
          this.syncToSelectedColor({ keepCurrentImage: true, skipWindowScroll: true });
        }, 400);
      }

      bindThumbnailClicks() {
        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((thumbnailItem) => {
          const button = thumbnailItem.querySelector('button');
          if (!button || button.dataset.kvThumbBound === 'true') return;

          button.dataset.kvThumbBound = 'true';

          button.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (thumbnailItem.hidden || thumbnailItem.classList.contains('is-hidden-by-alt-group')) return;

            const targetId = thumbnailItem.dataset.target;
            const targetGroup = thumbnailItem.dataset.mediaColor;

            if (!targetId) return;

            this.lockScrollSync(450);

            this.setActiveThumbnail(thumbnailItem, {
              scrollThumbs: false,
              lockSync: true,
            });

            if (targetGroup) {
              this.filterMediaByGroup(targetGroup, targetId, {
                keepCurrentImage: false,
                skipWindowScroll: true,
                fromThumbnailClick: true,
              });
            } else {
              this.setActiveMedia(targetId, false, {
                skipWindowScroll: true,
                fromThumbnailClick: true,
              });
            }
          });
        });
      }

      bindVariantAndSwatchChanges() {
        const runSync = (event) => {
          if (
            event.target &&
            event.target.closest &&
            event.target.closest('slider-component[id^="GalleryThumbnails"], slider-component[id^="GalleryViewer"]')
          ) {
            return;
          }

          window.setTimeout(() => {
            this.syncToSelectedColor({ keepCurrentImage: true, skipWindowScroll: true });
          }, 180);
        };

        document.addEventListener('variant:change', runSync);
        document.addEventListener('change', runSync);
        document.addEventListener('click', runSync);
      }

      lockScrollSync(duration = 350) {
        this.ignoreGalleryScrollUntil = Date.now() + duration;
      }

      shouldIgnoreScrollSync() {
        return this.isProgrammaticChange || Date.now() < this.ignoreGalleryScrollUntil;
      }

      onSlideChanged(event) {
        if (this.shouldIgnoreScrollSync()) return;

        const currentElement = event.detail?.currentElement;
        if (!currentElement) return;

        if (currentElement.hidden || currentElement.classList.contains('is-hidden-by-alt-group')) {
          this.syncToSelectedColor({ keepCurrentImage: true, skipWindowScroll: true });
          return;
        }

        const mediaId = currentElement.dataset.mediaId;
        if (!mediaId || !this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${CSS.escape(mediaId)}"]`);
        this.setActiveThumbnail(thumbnail, { scrollThumbs: true });
      }

      syncToSelectedColor(options = {}) {
        const selectedColor = this.getSelectedColor();

        if (selectedColor) {
          this.filterMediaByGroup(selectedColor, null, options);
          return;
        }

        const activeGroup = this.getActiveAltGroup();
        if (activeGroup) {
          this.filterMediaByGroup(activeGroup, null, options);
        }
      }

      getSelectedColor() {
        return (
          this.getSelectedColorFromDawnInputs() ||
          this.getSelectedColorFromGloboSwatches() ||
          this.getSelectedColorFromVisibleColorText()
        );
      }

      getSelectedColorFromDawnInputs() {
        const root = this.closest('product-info') || document;
        const fieldsets = Array.from(root.querySelectorAll('fieldset'));

        for (const fieldset of fieldsets) {
          const legend = fieldset.querySelector('legend')?.textContent?.trim()?.toLowerCase() || '';
          const isColorFieldset = this.colorOptionNames.some((name) => legend.includes(name));

          if (!isColorFieldset) continue;

          const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
          if (checkedInput?.value) return this.handleize(checkedInput.value);
        }

        const wrappers = Array.from(root.querySelectorAll('.product-form__input'));

        for (const wrapper of wrappers) {
          const label = wrapper.querySelector('label')?.textContent?.trim()?.toLowerCase() || '';
          const isColorSelect = this.colorOptionNames.some((name) => label.includes(name));

          if (!isColorSelect) continue;

          const select = wrapper.querySelector('select');
          if (select?.value) return this.handleize(select.value);
        }

        return null;
      }

      getSelectedColorFromGloboSwatches() {
        const selectedCandidates = Array.from(
          document.querySelectorAll(
            '.globo-swatch-list .selected, .globo-swatch-list .active, .globo-swatch-list [aria-checked="true"], .globo-swatch-list input:checked, .gpo-selected, .gpo-swatches input:checked'
          )
        );

        for (const item of selectedCandidates) {
          const value =
            item.value ||
            item.dataset.value ||
            item.dataset.optionValue ||
            item.getAttribute('data-value') ||
            item.getAttribute('title') ||
            item.getAttribute('aria-label') ||
            item.textContent;

          if (value && value.trim()) return this.handleize(value);
        }

        return null;
      }

      getSelectedColorFromVisibleColorText() {
        const root = this.closest('product-info') || document;
        const text = root.textContent || '';

        const colorMatch = text.match(/Color:\s*([A-Za-zÆØÅæøå\s-]+)/i);
        if (!colorMatch || !colorMatch[1]) return null;

        const rawColor = colorMatch[1].split('\n')[0].trim();
        if (!rawColor) return null;

        return this.handleize(rawColor);
      }

      getActiveAltGroup() {
        if (!this.elements.viewer) return null;

        const activeMedia =
          this.elements.viewer.querySelector('[data-media-id].is-active:not(.is-hidden-by-alt-group)') ||
          this.elements.viewer.querySelector('[data-media-id]:not(.is-hidden-by-alt-group)') ||
          this.elements.viewer.querySelector('[data-media-id]');

        return activeMedia?.dataset?.mediaColor || null;
      }

      filterMediaByGroup(group, preferredMediaId = null, options = {}) {
        if (!this.elements.viewer || !group) return;

        const keepCurrentImage = options.keepCurrentImage !== false;
        const mediaItems = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const thumbnailItems = Array.from(this.elements.thumbnails?.querySelectorAll('[data-target]') || []);

        const currentActive = this.elements.viewer.querySelector('[data-media-id].is-active');
        const currentActiveId = currentActive?.dataset.mediaId;

        let firstMatchId = null;
        let preferredMatchId = null;
        let currentMatchId = null;

        this.isProgrammaticChange = true;

        mediaItems.forEach((item) => {
          const shouldShow = item.dataset.mediaColor === group;

          item.hidden = !shouldShow;
          item.classList.toggle('is-hidden-by-alt-group', !shouldShow);

          if (shouldShow && !firstMatchId) {
            firstMatchId = item.dataset.mediaId;
          }

          if (shouldShow && preferredMediaId && item.dataset.mediaId === preferredMediaId) {
            preferredMatchId = item.dataset.mediaId;
          }

          if (shouldShow && keepCurrentImage && currentActiveId && item.dataset.mediaId === currentActiveId) {
            currentMatchId = item.dataset.mediaId;
          }
        });

        thumbnailItems.forEach((item) => {
          const shouldShow = item.dataset.mediaColor === group;

          item.hidden = !shouldShow;
          item.classList.toggle('is-hidden-by-alt-group', !shouldShow);
        });

        const nextMediaId = preferredMatchId || currentMatchId || firstMatchId;

        if (!nextMediaId) {
          this.isProgrammaticChange = false;
          return;
        }

        this.currentGroup = group;

        this.setActiveMedia(nextMediaId, false, {
          skipWindowScroll: options.skipWindowScroll !== false,
          fromThumbnailClick: options.fromThumbnailClick === true,
        });

        if (this.elements.viewer?.resetPages) {
          this.elements.viewer.resetPages();
        }

        window.setTimeout(() => {
          this.isProgrammaticChange = false;
        }, 80);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        if (!this.elements.viewer || !mediaId) return;

        const { skipWindowScroll = false, fromThumbnailClick = false } = options;

        if (fromThumbnailClick) {
          this.lockScrollSync(450);
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${CSS.escape(mediaId)}"]:not(.is-hidden-by-alt-group)`) ||
          this.elements.viewer.querySelector('[data-media-id]:not(.is-hidden-by-alt-group)') ||
          this.elements.viewer.querySelector('[data-media-id]');

        if (!activeMedia) return;

        const nextMediaId = activeMedia.dataset.mediaId;

        this.isProgrammaticChange = true;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });

        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${CSS.escape(nextMediaId)}"]`);

        if (activeThumbnail) {
          this.setActiveThumbnail(activeThumbnail, {
            scrollThumbs: !fromThumbnailClick,
            lockSync: fromThumbnailClick,
          });
        }

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (activeThumbnail) {
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();

        window.requestAnimationFrame(() => {
          if (activeMedia.parentElement) {
            activeMedia.parentElement.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: 'auto',
            });
          }

          this.playActiveMedia(activeMedia);

          if (activeThumbnail) {
            this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
          }

          window.setTimeout(() => {
            this.isProgrammaticChange = false;
          }, 100);

          if (skipWindowScroll) return;

          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;

          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      }

      setActiveThumbnail(thumbnail, options = {}) {
        if (!this.elements.thumbnails || !thumbnail) return;

        const { scrollThumbs = true, lockSync = false } = options;

        if (lockSync) {
          this.lockScrollSync(350);
        }

        const buttons = this.elements.thumbnails.querySelectorAll('button.thumbnail, .thumbnail button, button');
        const items = this.elements.thumbnails.querySelectorAll('.thumbnail-list__item');

        items.forEach((item) => {
          item.classList.toggle('is-active-thumbnail', item === thumbnail);
        });

        buttons.forEach((button) => {
          const ownsActiveItem = thumbnail.contains(button);
          if (ownsActiveItem) {
            button.setAttribute('aria-current', 'true');
          } else {
            button.removeAttribute('aria-current');
          }
        });

        if (!scrollThumbs) return;

        if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        const slider = this.elements.thumbnails.slider || this.elements.thumbnails.querySelector('ul, .thumbnail-list');

        if (slider?.scrollTo) {
          slider.scrollTo({
            left: Math.max(thumbnail.offsetLeft - 8, 0),
            behavior: 'auto',
          });
        }
      }

      handleize(value) {
        return value
          .toString()
          .trim()
          .toLowerCase()
          .replace(/æ/g, 'ae')
          .replace(/ø/g, 'oe')
          .replace(/å/g, 'aa')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion || !window.accessibilityStrings?.imageAvailable) return;

        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);

          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };

        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (window.pauseAllMedia) {
          window.pauseAllMedia();
        }

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;

        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;

        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      injectKlaraThumbnailOverlayStyle() {
        if (document.getElementById('kv-gallery-thumbnail-overlay-fix')) return;

        const style = document.createElement('style');
        style.id = 'kv-gallery-thumbnail-overlay-fix';
        style.textContent = `
          @media screen and (max-width: 749px) {
            product-info[id^="MainProduct-"] .thumbnail-list__item:not([hidden]) {
              opacity: 0.40 !important;
              transition: opacity 0.08s linear !important;
            }

            product-info[id^="MainProduct-"] .thumbnail-list__item.is-active-thumbnail,
            product-info[id^="MainProduct-"] .thumbnail-list__item:has(.thumbnail[aria-current]),
            product-info[id^="MainProduct-"] .thumbnail-list__item:has(button[aria-current]) {
              opacity: 1 !important;
            }

            product-info[id^="MainProduct-"] .thumbnail::after,
            product-info[id^="MainProduct-"] .thumbnail-list__item::after,
            product-info[id^="MainProduct-"] .thumbnail.global-media-settings::after,
            product-info[id^="MainProduct-"] button.thumbnail.global-media-settings.global-media-settings--no-shadow::after {
              content: none !important;
              display: none !important;
              background: transparent !important;
            }
          }
        `;

        document.head.appendChild(style);
      }
    }
  );
}

/* KLARA & VORN - STABLE MOBILE GALLERY / THUMBNAIL SYNC */
(function () {
  function initKlaraGallerySync(gallery) {
    if (!gallery || gallery.dataset.kvScrollSyncInit === 'true') return;

    const viewer =
      gallery.querySelector('ul[id^="Slider-Gallery"]') ||
      gallery.querySelector('.product__media-list');

    const thumbnailsComponent = gallery.querySelector('slider-component[id^="GalleryThumbnails"]');
    const thumbnails =
      gallery.querySelector('ul[id^="Slider-Thumbnails"]') ||
      gallery.querySelector('.thumbnail-list');

    if (!viewer || !thumbnails) return;

    gallery.dataset.kvScrollSyncInit = 'true';

    let ticking = false;
    let lastActiveMediaId = null;
    let userIsScrollingViewer = false;
    let scrollEndTimer = null;

    function isLocked() {
      return gallery.shouldIgnoreScrollSync && gallery.shouldIgnoreScrollSync();
    }

    function getVisibleSlides() {
      return Array.from(viewer.querySelectorAll('[data-media-id]')).filter((slide) => {
        return (
          !slide.hidden &&
          !slide.classList.contains('is-hidden-by-alt-group') &&
          !slide.classList.contains('is-hidden-by-color') &&
          slide.offsetParent !== null
        );
      });
    }

    function getClosestSlide() {
      const slides = getVisibleSlides();
      if (!slides.length) return null;

      const viewerRect = viewer.getBoundingClientRect();
      const viewerCenter = viewerRect.left + viewerRect.width / 2;

      let closest = slides[0];
      let closestDistance = Infinity;

      slides.forEach((slide) => {
        const rect = slide.getBoundingClientRect();
        const slideCenter = rect.left + rect.width / 2;
        const distance = Math.abs(slideCenter - viewerCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closest = slide;
        }
      });

      return closest;
    }

    function setActiveThumbnailFromScroll(mediaId) {
      if (!mediaId || mediaId === lastActiveMediaId || isLocked()) return;

      const activeThumbnail = thumbnails.querySelector(`[data-target="${CSS.escape(mediaId)}"]`);

      if (!activeThumbnail || activeThumbnail.hidden || activeThumbnail.classList.contains('is-hidden-by-alt-group')) {
        return;
      }

      lastActiveMediaId = mediaId;

      viewer.querySelectorAll('[data-media-id]').forEach((slide) => {
        slide.classList.toggle('is-active', slide.dataset.mediaId === mediaId);
      });

      thumbnails.querySelectorAll('.thumbnail-list__item').forEach((item) => {
        item.classList.toggle('is-active-thumbnail', item === activeThumbnail);
      });

      thumbnails.querySelectorAll('button.thumbnail, .thumbnail button, button').forEach((button) => {
        if (activeThumbnail.contains(button)) {
          button.setAttribute('aria-current', 'true');
        } else {
          button.removeAttribute('aria-current');
        }
      });

      const slider = thumbnailsComponent?.slider || thumbnails;

      if (slider?.scrollTo) {
        slider.scrollTo({
          left: Math.max(activeThumbnail.offsetLeft - 8, 0),
          behavior: 'auto',
        });
      }
    }

    function syncFromScroll() {
      ticking = false;

      if (isLocked()) return;

      const closestSlide = getClosestSlide();
      if (!closestSlide) return;

      setActiveThumbnailFromScroll(closestSlide.dataset.mediaId);
    }

    function requestSync() {
      if (isLocked()) return;
      if (ticking) return;

      ticking = true;
      window.requestAnimationFrame(syncFromScroll);
    }

    function markUserScroll() {
      userIsScrollingViewer = true;
      window.clearTimeout(scrollEndTimer);

      scrollEndTimer = window.setTimeout(() => {
        userIsScrollingViewer = false;
        requestSync();
      }, 120);
    }

    viewer.addEventListener(
      'scroll',
      function () {
        if (isLocked()) return;
        markUserScroll();
        requestSync();
      },
      { passive: true }
    );

    viewer.addEventListener('touchend', requestSync, { passive: true });
    viewer.addEventListener('pointerup', requestSync, { passive: true });
    window.addEventListener('resize', requestSync, { passive: true });

    if ('onscrollend' in window) {
      viewer.addEventListener(
        'scrollend',
        function () {
          if (!userIsScrollingViewer) return;
          requestSync();
        },
        { passive: true }
      );
    }

    const observer = new MutationObserver(() => {
      if (isLocked()) return;
      window.setTimeout(requestSync, 80);
    });

    observer.observe(viewer, {
      attributes: true,
      subtree: true,
      attributeFilter: ['hidden', 'class'],
    });

    window.setTimeout(requestSync, 250);
  }

  function initAllKlaraGallerySync() {
    document.querySelectorAll('media-gallery').forEach(initKlaraGallerySync);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllKlaraGallerySync);
  } else {
    initAllKlaraGallerySync();
  }

  document.addEventListener('shopify:section:load', initAllKlaraGallerySync);
  document.addEventListener('variant:change', function () {
    window.setTimeout(initAllKlaraGallerySync, 150);
  });
})();